import React, { useState, useRef, useEffect } from 'react'
import { formatISTDate } from '../../components/lib/dateUtils'

export interface ChatHistoryItem {
  id: string
  title: string
  timestamp?: Date
}

interface SidebarProps {
  open: boolean
  onToggle: () => void
  onClose: () => void
  activeChat: string | number
  chatTitle: string
  onNewChat: () => void
  onLoadChat: (id: string | number, label: string) => void
  onOpenGuide: () => void
  onOpenProfile: () => void
  onOpenTC: () => void
  hasHistory: boolean
  chatHistories?: ChatHistoryItem[]
  currentChatTimestamp?: Date
  userDisplayName?: string
  userSubtitle?: string
  userInitials?: string
  onRenameChat?: (chatId: string) => void
  onDeleteChat?: (chatId: string) => void
  /** When true, show "Load older chats" in the sidebar list */
  hasMoreSessions?: boolean
  loadingMoreSessions?: boolean
  onLoadMoreSessions?: () => void
}

function isToday(d: Date): boolean {
  const today = new Date()
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  )
}

function ChatItem({
  label,
  dateTimeLabel,
  active,
  onClick,
  chatId,
  onRename,
  onDelete,
}: {
  label: string
  dateTimeLabel?: string
  active: boolean
  onClick: () => void
  chatId?: string
  onRename?: () => void
  onDelete?: () => void
}) {
  const [hov, setHov] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpen])

  const showMenu = onRename && onDelete && chatId

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '7px 9px',
        borderRadius: 7,
        cursor: 'pointer',
        marginBottom: 1,
        background: active ? 'rgba(255,255,255,.1)' : hov ? 'rgba(255,255,255,.07)' : 'transparent',
        border: active ? '1px solid rgba(255,255,255,.12)' : '1px solid transparent',
        transition: 'all .18s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, flexShrink: 0, opacity: 0.55, color: 'var(--white)' }}></span>
        <span
          style={{
            flex: 1,
            fontSize: 12,
            color: active ? 'var(--white)' : 'var(--on-navy-soft)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: active ? 500 : 400,
          }}
        >
          {label}
        </span>
      {showMenu && (
        <div
          ref={menuRef}
          style={{
            position: 'relative',
            flexShrink: 0,
            opacity: hov || menuOpen ? 1 : 0,
            transition: 'opacity .18s',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen((o) => !o)
            }}
            style={{
              padding: 2,
              background: 'transparent',
              border: 'none',
              color: 'var(--on-navy-muted)',
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
            }}
            aria-label="Chat menu"
          >
            ⋮
          </button>
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 2,
                background: 'var(--navy2)',
                border: '1px solid rgba(255,255,255,.15)',
                borderRadius: 8,
                boxShadow: 'var(--sh)',
                zIndex: 10,
                minWidth: 100,
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onRename?.()
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 12,
                  color: 'var(--white)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                Rename
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onDelete?.()
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 12,
                  color: 'var(--white)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderTop: '1px solid rgba(255,255,255,.08)',
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}
      </div>
      {dateTimeLabel && (
        <span
          style={{
            display: 'none',
            fontSize: 10,
            color: 'var(--on-navy-dim)',
            paddingLeft: 17,
          }}
        >
          {dateTimeLabel}
        </span>
      )}
    </div>
  )
}

function SbRow({
  children,
  onClick,
}: {
  children: React.ReactNode
  onClick: () => void
}) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 9px',
        borderRadius: 7,
        cursor: 'pointer',
        marginBottom: 5,
        background: hov ? 'rgba(255,255,255,.07)' : 'transparent',
        transition: 'background .18s',
      }}
    >
      {children}
    </div>
  )
}

export default function Sidebar({
  open,
  onToggle,
  onClose,
  activeChat,
  chatTitle,
  onNewChat,
  onLoadChat,
  onOpenGuide,
  onOpenProfile,
  onOpenTC,
  hasHistory,
  chatHistories = [],
  currentChatTimestamp,
  userDisplayName = 'User',
  userSubtitle = 'Quantum Leap · Achiever',
  userInitials = 'U',
  onRenameChat,
  onDeleteChat,
  hasMoreSessions = false,
  loadingMoreSessions = false,
  onLoadMoreSessions,
}: SidebarProps) {
  const [search, setSearch] = useState('')
  const filtered = chatHistories.filter(
    (c) => !search || c.title.toLowerCase().includes(search.toLowerCase())
  )
  const curLabel = chatTitle || 'New conversation'
  const curVisible = !search || curLabel.toLowerCase().includes(search.toLowerCase())
  const currentInFiltered = filtered.some((c) => String(c.id) === String(activeChat))
  const todayList = filtered.filter((c) => c.timestamp && isToday(c.timestamp))
  const earlierList = filtered.filter((c) => !c.timestamp || !isToday(c.timestamp))
  const showCurrentAsNew = curVisible && !currentInFiltered
  /** Draft / welcome (no session yet) — do not list "New conversation" in the sidebar */
  const isDraftSelection = activeChat === 0 || activeChat === '0'
  const showCurrentRow = showCurrentAsNew && !isDraftSelection
  const currentTimestamp = currentChatTimestamp ?? new Date()

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth <= 1024
    return false
  })

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1024px)')
    setIsMobile(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return (
    <>
      {open && isMobile && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99,
            background: 'rgba(0,0,0,.22)',
          }}
        />
      )}
      <div
        style={{
          width: 'var(--sb-w)',
          height: '100vh',
          background: 'var(--navy)',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 100,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform .3s cubic-bezier(.16,1,.3,1)',
          boxShadow: open ? '4px 0 24px rgba(0,0,0,.18)' : 'none',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 33,
                height: 33,
                background: 'linear-gradient(135deg,var(--gold),#E8C96A)',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Playfair Display',serif",
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--navy)',
                flexShrink: 0,
              }}
            >
              A
            </div>
            <div>
              <span
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--white)',
                  display: 'block',
                  lineHeight: 1,
                }}
              >
                Athena{' '}
                <span
                  style={{
                    display: 'inline-flex',
                    background: 'rgba(201,168,76,.15)',
                    border: '1px solid rgba(201,168,76,.3)',
                    borderRadius: 100,
                    padding: '2px 7px',
                    fontSize: 8.5,
                    fontWeight: 600,
                    color: 'var(--gold)',
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Beta
                </span>
              </span>
              <span
                style={{
                  fontSize: 8.5,
                  color: 'rgba(201,168,76,.8)',
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  display: 'block',
                  marginTop: 2,
                }}
              >
                Strategy · Intelligence
              </span>
            </div>
          </div>
          <button
            onClick={onNewChat}
            type="button"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              padding: '8px 12px',
              background: 'rgba(255,255,255,.08)',
              border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 9,
              color: 'var(--white)',
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Chat
          </button>
        </div>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              background: 'rgba(255,255,255,.07)',
              border: '1px solid rgba(255,255,255,.1)',
              borderRadius: 8,
              padding: '6px 10px',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--on-navy-faint)"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats…"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 12,
                color: 'var(--white)',
                width: '100%',
              }}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 8px' }}>
          {(showCurrentRow || todayList.length > 0) && (
            <>
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 500,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: 'var(--on-navy-dim)',
                  padding: '6px 8px 4px',
                }}
              >
                Today
              </div>
              {showCurrentRow && (
                <ChatItem
                  label={curLabel}
                  dateTimeLabel={formatISTDate(currentTimestamp)}
                  active={true}
                  onClick={() => onLoadChat(activeChat, curLabel)}
                  chatId={activeChat !== 0 && activeChat !== '0' ? String(activeChat) : undefined}
                  onRename={
                    onRenameChat && activeChat !== 0 && activeChat !== '0'
                      ? () => onRenameChat(String(activeChat))
                      : undefined
                  }
                  onDelete={
                    onDeleteChat && activeChat !== 0 && activeChat !== '0'
                      ? () => onDeleteChat(String(activeChat))
                      : undefined
                  }
                />
              )}
              {todayList.map((c) => (
                <ChatItem
                  key={c.id}
                  label={c.title}
                  dateTimeLabel={c.timestamp ? formatISTDate(c.timestamp) : undefined}
                  active={String(activeChat) === String(c.id)}
                  onClick={() => onLoadChat(c.id, c.title)}
                  chatId={c.id}
                  onRename={onRenameChat ? () => onRenameChat(c.id) : undefined}
                  onDelete={onDeleteChat ? () => onDeleteChat(c.id) : undefined}
                />
              ))}
            </>
          )}
          {earlierList.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 500,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: 'var(--on-navy-dim)',
                  padding: '14px 8px 4px',
                }}
              >
                Earlier
              </div>
              {earlierList.map((c) => (
                <ChatItem
                  key={c.id}
                  label={c.title}
                  dateTimeLabel={c.timestamp ? formatISTDate(c.timestamp) : undefined}
                  active={String(activeChat) === String(c.id)}
                  onClick={() => onLoadChat(c.id, c.title)}
                  chatId={c.id}
                  onRename={onRenameChat ? () => onRenameChat(c.id) : undefined}
                  onDelete={onDeleteChat ? () => onDeleteChat(c.id) : undefined}
                />
              ))}
            </>
          )}
          {hasMoreSessions && onLoadMoreSessions && (
            <div style={{ padding: '8px 6px 4px' }}>
              <button
                type="button"
                onClick={() => onLoadMoreSessions()}
                disabled={loadingMoreSessions}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: 'var(--on-navy-muted)',
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(255,255,255,.1)',
                  borderRadius: 8,
                  cursor: loadingMoreSessions ? 'wait' : 'pointer',
                  opacity: loadingMoreSessions ? 0.7 : 1,
                }}
              >
                {loadingMoreSessions ? 'Loading…' : 'Load older chats'}
              </button>
            </div>
          )}
        </div>
        <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <SbRow onClick={onOpenGuide}>
            <div
              style={{
                width: 24,
                height: 24,
                background: 'rgba(201,168,76,.15)',
                border: '1px solid rgba(201,168,76,.25)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: 'var(--gold)',
              }}
            >
              ✦
            </div>
            <span style={{ fontSize: 12, color: 'var(--on-navy-muted)' }}>
              Guides — How to use Athena
            </span>
          </SbRow>
          <SbRow onClick={onOpenProfile}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'rgba(255,255,255,.12)',
                border: '1.5px solid rgba(255,255,255,.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9.5,
                fontWeight: 500,
                color: 'var(--white)',
                flexShrink: 0,
              }}
            >
              {userInitials}
            </div>
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: 'var(--white)',
                  display: 'block',
                  lineHeight: 1,
                }}
              >
                {userDisplayName}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--on-navy-faint)',
                  display: 'block',
                  marginTop: 1,
                }}
              >
                {userSubtitle}
              </span>
            </div>
            <span style={{ color: 'var(--on-navy-dim)', fontSize: 13 }}>⋯</span>
          </SbRow>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(37,99,235,0.15)',
              border: '1px solid rgba(37,99,235,0.35)',
              borderRadius: 100,
              padding: '5px 10px',
              marginBottom: 8,
              fontSize: 10,
              color: 'rgba(147,197,253,0.95)',
            }}
          >
            <div
              style={{
                width: 5,
                height: 5,
                background: '#22C55E',
                borderRadius: '50%',
                flexShrink: 0,
                animation: 'glow 2.5s ease-in-out infinite',
              }}
            />
            Business context loaded
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '5px 9px 2px',
            }}
          >
            <span
              onClick={onOpenTC}
              onKeyDown={(e) => e.key === 'Enter' && onOpenTC()}
              role="button"
              tabIndex={0}
              style={{ fontSize: 10.5, color: 'var(--on-navy-dim)', cursor: 'pointer' }}
            >
              Terms of Use
            </span>
            <span style={{ color: 'var(--on-navy-dot)', fontSize: 10 }}>·</span>
            <span style={{ fontSize: 10.5, color: 'var(--gold-muted)' }}>Beta v0.1</span>
          </div>
        </div>
      </div>
      <div
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        role="button"
        tabIndex={0}
        style={{
          position: 'fixed',
          left: open ? 'var(--sb-w)' : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 22,
          height: 56,
          background: 'var(--navy)',
          borderRadius: '0 10px 10px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 101,
          transition: 'left .3s cubic-bezier(.16,1,.3,1)',
          boxShadow: '2px 0 12px rgba(0,0,0,.15)',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--on-navy-soft)"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          {open ? (
            <polyline points="15 18 9 12 15 6" />
          ) : (
            <polyline points="9 18 15 12 9 6" />
          )}
        </svg>
      </div>
    </>
  )
}
